import { useEffect, useState, useMemo } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

export default function ProductManualPage() {
  const [markdown, setMarkdown] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [query, setQuery] = useState('');

  useEffect(() => {
    let cancelled = false;
    fetch('/product-manual.md', { cache: 'no-cache' })
      .then((r) => (r.ok ? r.text() : Promise.reject(new Error(`HTTP ${r.status}`))))
      .then((t) => { if (!cancelled) setMarkdown(t); })
      .catch((e) => { if (!cancelled) setError(e.message); });
    return () => { cancelled = true; };
  }, []);

  const sections = useMemo(() => {
    if (!markdown) return [];
    const result: Array<{ title: string; anchor: string }> = [];
    for (const line of markdown.split(/\r?\n/)) {
      const m = /^##\s+(\d+\.\s+.+?)\s*$/.exec(line);
      if (m) {
        const title = m[1];
        const anchor = title.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
        result.push({ title, anchor });
      }
    }
    return result;
  }, [markdown]);

  const filteredSections = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return sections;
    return sections.filter((s) => s.title.toLowerCase().includes(q));
  }, [sections, query]);

  return (
    <div className="p-6 max-w-6xl mx-auto">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Product manual</h1>
          <div className="text-xs text-slate-500">Full SupportByte user guide · always up to date</div>
        </div>
        <button
          onClick={() => window.print()}
          className="btn-ghost"
        >
          Print / Save as PDF
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[260px_1fr] gap-6">
        <aside className="lg:sticky lg:top-4 self-start">
          <div className="card p-3">
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Jump to section…"
              className="input mb-2 text-sm"
            />
            <nav className="max-h-[70vh] overflow-y-auto">
              {!filteredSections.length ? (
                <div className="text-xs text-slate-400 px-2 py-3">No sections match.</div>
              ) : (
                <ul className="space-y-0.5">
                  {filteredSections.map((s) => (
                    <li key={s.anchor}>
                      <a
                        href={`#${s.anchor}`}
                        className="block px-2 py-1 text-xs text-slate-600 rounded hover:bg-emerald-50 hover:text-emerald-700 transition"
                      >
                        {s.title}
                      </a>
                    </li>
                  ))}
                </ul>
              )}
            </nav>
          </div>
        </aside>

        <div className="card p-6 lg:p-8">
          {error ? (
            <div className="text-center text-slate-500 py-12">
              <div className="font-medium">Couldn't load the manual</div>
              <div className="text-xs mt-1">{error}</div>
            </div>
          ) : !markdown ? (
            <div className="text-slate-500 text-sm">Loading manual…</div>
          ) : (
            <article className="help-prose">
              <ReactMarkdown
                remarkPlugins={[remarkGfm]}
                components={{
                  h2: ({ children, ...props }) => {
                    const text = String(children);
                    const m = /^(\d+\.\s+.+)$/.exec(text);
                    const anchor = m
                      ? m[1].toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '')
                      : undefined;
                    return <h2 id={anchor} {...props}>{children}</h2>;
                  },
                }}
              >
                {markdown}
              </ReactMarkdown>
            </article>
          )}
        </div>
      </div>
    </div>
  );
}
